import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// Base interfaces for Sale Statistics Tab
export interface SaleReportRecord {
  id: string;
  orderId: string;
  orderDate: string;
  orderStatus: string;
  paymentMethod: string;
  channel: string;
  grossSales: number;
  discount: number;
  refund: number;
  netSales: number;
  tax: number;
  shippingFee: number;
  totalCollected: number;
  numberOfOrders: number;
  averageOrderValue: number;
}

// Base interfaces for Sales Analysis Tab
export interface SalesAnalysisRecord {
  id: string;
  datePeriod: string;
  channel: string;
  regionArea: string;
  totalOrders: number;
  grossSales: number;
  netSales: number;
  refund: number;
  averageOrderValue: number;
  conversionRate: number;
  salesGrowthRate: number;
}

// Base interfaces for Product Sales Tab
export interface ProductSalesRecord {
  id: string;
  productId: string;
  productName: string;
  category: string;
  brand: string;
  quantitySold: number;
  returnedQuantity: number;
  unitPrice: number;
  grossSales: number;
  discountApplied: number;
  netSales: number;
  topSellingFlag: boolean;
}

// Base interfaces for Shop Sales Tab
export interface ShopSalesRecord {
  id: string;
  shopStoreName: string;
  channel: string;
  region: string;
  numberOfOrders: number;
  grossSales: number;
  netSales: number;
  refund: number;
  shippingFee: number;
  topSellingProducts: string;
  salesGrowthRate: number;
}

export interface SaleReportSearchParams {
  startDate?: string;
  endDate?: string;
  orderStatus?: string;
  paymentMethod?: string;
  channel?: string;
  regionArea?: string;
  productName?: string;
  productId?: string;
  category?: string;
  brand?: string;
  topSellingFlag?: string;
  shopStoreName?: string;
  status?: string;
  page?: number;
  size?: number;
}

export interface SaleReportResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Statistics Overview Data
export interface StatisticsOverview {
  totalOrders: number;
  totalSales: number;
  totalItems: number;
  averageOrder: number;
  conversionRate: number;
  totalCustomers: number;
}

// Search configurations for different report types
export const SALES_STATISTICS_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'dateRange',
    label: 'Date Range',
    type: 'dateRange',
    range: { startKey: 'startDate', endKey: 'endDate' },
  },
  {
    key: 'orderStatus',
    label: 'Order Status',
    type: 'select',
    placeholder: 'All Statuses',
    options: [
      { label: 'All Statuses', value: 'ALL' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Confirmed', value: 'CONFIRMED' },
      { label: 'Processing', value: 'PROCESSING' },
      { label: 'Shipped', value: 'SHIPPED' },
      { label: 'Delivered', value: 'DELIVERED' },
      { label: 'Cancelled', value: 'CANCELLED' },
      { label: 'Refunded', value: 'REFUNDED' },
    ],
  },
  {
    key: 'paymentMethod',
    label: 'Payment Method',
    type: 'select',
    placeholder: 'All Methods',
    options: [
      { label: 'All Methods', value: 'ALL' },
      { label: 'Credit Card', value: 'CREDIT_CARD' },
      { label: 'eWallet', value: 'EWALLET' },
      { label: 'COD', value: 'COD' },
      { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
      { label: 'Other', value: 'OTHER' },
    ],
  },
  {
    key: 'channel',
    label: 'Channel',
    type: 'select',
    placeholder: 'All Channels',
    options: [
      { label: 'All Channels', value: 'ALL' },
      { label: 'Online Store', value: 'ONLINE' },
      { label: 'Mobile App', value: 'MOBILE' },
      { label: 'Physical Store', value: 'PHYSICAL' },
      { label: 'Marketplace', value: 'MARKETPLACE' },
    ],
  },
];

export const SALES_ANALYSIS_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'timeDimension',
    label: 'Time Dimension',
    type: 'select',
    placeholder: 'Daily',
    options: [
      { label: 'Daily', value: 'DAILY' },
      { label: 'Weekly', value: 'WEEKLY' },
      { label: 'Monthly', value: 'MONTHLY' },
      { label: 'Yearly', value: 'YEARLY' },
    ],
  },
  {
    key: 'channel',
    label: 'Channel',
    type: 'select',
    placeholder: 'All Channels',
    options: [
      { label: 'All Channels', value: 'ALL' },
      { label: 'Online Store', value: 'ONLINE' },
      { label: 'Mobile App', value: 'MOBILE' },
      { label: 'Physical Store', value: 'PHYSICAL' },
      { label: 'Marketplace', value: 'MARKETPLACE' },
    ],
  },
  {
    key: 'regionArea',
    label: 'Region / Area',
    type: 'select',
    placeholder: 'All Regions',
    options: [
      { label: 'All Regions', value: 'ALL' },
      { label: 'North America', value: 'NORTH_AMERICA' },
      { label: 'Europe', value: 'EUROPE' },
      { label: 'Asia Pacific', value: 'ASIA_PACIFIC' },
      { label: 'Latin America', value: 'LATIN_AMERICA' },
      { label: 'Middle East', value: 'MIDDLE_EAST' },
      { label: 'Africa', value: 'AFRICA' },
    ],
  },
];

export const PRODUCT_SALES_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'productName',
    label: 'Product Name',
    type: 'text',
    placeholder: 'Enter keywords',
  },
  {
    key: 'productId',
    label: 'Product ID / SKU',
    type: 'text',
    placeholder: 'Enter code',
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    placeholder: 'All Categories',
    options: [
      { label: 'All Categories', value: 'ALL' },
      { label: 'Electronics', value: 'ELECTRONICS' },
      { label: 'Clothing', value: 'CLOTHING' },
      { label: 'Home & Garden', value: 'HOME_GARDEN' },
      { label: 'Sports', value: 'SPORTS' },
      { label: 'Beauty', value: 'BEAUTY' },
      { label: 'Books', value: 'BOOKS' },
      { label: 'Automotive', value: 'AUTOMOTIVE' },
    ],
  },
  {
    key: 'brand',
    label: 'Brand',
    type: 'select',
    placeholder: 'All Brands',
    options: [
      { label: 'All Brands', value: 'ALL' },
      { label: 'Apple', value: 'APPLE' },
      { label: 'Samsung', value: 'SAMSUNG' },
      { label: 'Nike', value: 'NIKE' },
      { label: 'Adidas', value: 'ADIDAS' },
      { label: 'Sony', value: 'SONY' },
      { label: 'LG', value: 'LG' },
      { label: 'Other', value: 'OTHER' },
    ],
  },
  {
    key: 'topSellingFlag',
    label: 'Top-selling Flag',
    type: 'select',
    placeholder: 'All Products',
    options: [
      { label: 'All Products', value: 'ALL' },
      { label: 'Top-selling Only', value: 'true' },
      { label: 'Non Top-selling', value: 'false' },
    ],
  },
];

export const SHOP_SALES_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'shopStoreName',
    label: 'Shop / Store Name',
    type: 'text',
    placeholder: 'Enter keywords',
  },
  {
    key: 'channel',
    label: 'Channel',
    type: 'select',
    placeholder: 'All Channels',
    options: [
      { label: 'All Channels', value: 'ALL' },
      { label: 'Online Store', value: 'ONLINE' },
      { label: 'Mobile App', value: 'MOBILE' },
      { label: 'Physical Store', value: 'PHYSICAL' },
      { label: 'Marketplace', value: 'MARKETPLACE' },
      { label: 'Franchise', value: 'FRANCHISE' },
    ],
  },
  {
    key: 'region',
    label: 'Region',
    type: 'select',
    placeholder: 'All Regions',
    options: [
      { label: 'All Regions', value: 'ALL' },
      { label: 'North America', value: 'NORTH_AMERICA' },
      { label: 'Europe', value: 'EUROPE' },
      { label: 'Asia Pacific', value: 'ASIA_PACIFIC' },
      { label: 'Latin America', value: 'LATIN_AMERICA' },
      { label: 'Middle East', value: 'MIDDLE_EAST' },
      { label: 'Africa', value: 'AFRICA' },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    placeholder: 'All Status',
    options: [
      { label: 'All Status', value: 'ALL' },
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Inactive', value: 'INACTIVE' },
      { label: 'Suspended', value: 'SUSPENDED' },
      { label: 'Under Review', value: 'UNDER_REVIEW' },
    ],
  },
];

// Table configurations for different report types
export const SALES_STATISTICS_TABLE_CONFIG: TableColumn[] = [
  { label: 'Order ID', prop: 'orderId', width: 120 },
  { label: 'Order Date', prop: 'orderDate', width: 120 },
  { label: 'Order Status', prop: 'orderStatus', width: 120 },
  { label: 'Payment Method', prop: 'paymentMethod', width: 130 },
  { label: 'Channel', prop: 'channel', width: 120 },
  { label: 'Gross Sales', prop: 'grossSales', width: 120, styleClass: 'text-right' },
  { label: 'Discount', prop: 'discount', width: 100, styleClass: 'text-right' },
  { label: 'Refund', prop: 'refund', width: 100, styleClass: 'text-right' },
  { label: 'Net Sales', prop: 'netSales', width: 120, styleClass: 'text-right' },
  { label: 'Tax', prop: 'tax', width: 80, styleClass: 'text-right' },
  { label: 'Shipping Fee', prop: 'shippingFee', width: 110, styleClass: 'text-right' },
  { label: 'Total Collected', prop: 'totalCollected', width: 130, styleClass: 'text-right' },
  { label: 'Number of Orders', prop: 'numberOfOrders', width: 130, styleClass: 'text-right' },
  { label: 'Average Order Value (AOV)', prop: 'averageOrderValue', width: 180, styleClass: 'text-right' },
];

export const SALES_ANALYSIS_TABLE_CONFIG: TableColumn[] = [
  { label: 'Date / Period', prop: 'datePeriod', width: 150 },
  { label: 'Channel', prop: 'channel', width: 120 },
  { label: 'Region / Area', prop: 'regionArea', width: 140 },
  { label: 'Total Orders', prop: 'totalOrders', width: 120, styleClass: 'text-right' },
  { label: 'Gross Sales', prop: 'grossSales', width: 120, styleClass: 'text-right' },
  { label: 'Net Sales', prop: 'netSales', width: 120, styleClass: 'text-right' },
  { label: 'Refund', prop: 'refund', width: 100, styleClass: 'text-right' },
  { label: 'Average Order Value (AOV)', prop: 'averageOrderValue', width: 180, styleClass: 'text-right' },
  { label: 'Conversion Rate (%)', prop: 'conversionRate', width: 150, styleClass: 'text-right' },
  { label: 'Sales Growth Rate (%)', prop: 'salesGrowthRate', width: 160, styleClass: 'text-right' },
];

export const PRODUCT_SALES_TABLE_CONFIG: TableColumn[] = [
  { label: 'Product ID / SKU', prop: 'productId', width: 150 },
  { label: 'Product Name', prop: 'productName', width: 200 },
  { label: 'Category', prop: 'category', width: 120 },
  { label: 'Brand', prop: 'brand', width: 120 },
  { label: 'Quantity Sold', prop: 'quantitySold', width: 130, styleClass: 'text-right' },
  { label: 'Returned Quantity', prop: 'returnedQuantity', width: 140, styleClass: 'text-right' },
  { label: 'Unit Price', prop: 'unitPrice', width: 110, styleClass: 'text-right' },
  { label: 'Gross Sales', prop: 'grossSales', width: 120, styleClass: 'text-right' },
  { label: 'Discount Applied', prop: 'discountApplied', width: 140, styleClass: 'text-right' },
  { label: 'Net Sales', prop: 'netSales', width: 120, styleClass: 'text-right' },
  { label: 'Top-selling Flag', prop: 'topSellingFlag', width: 140 },
];

export const SHOP_SALES_TABLE_CONFIG: TableColumn[] = [
  { label: 'Shop / Store Name', prop: 'shopStoreName', width: 180 },
  { label: 'Channel', prop: 'channel', width: 120 },
  { label: 'Region', prop: 'region', width: 120 },
  { label: 'Number of Orders', prop: 'numberOfOrders', width: 140, styleClass: 'text-right' },
  { label: 'Gross Sales', prop: 'grossSales', width: 120, styleClass: 'text-right' },
  { label: 'Net Sales', prop: 'netSales', width: 120, styleClass: 'text-right' },
  { label: 'Refund', prop: 'refund', width: 100, styleClass: 'text-right' },
  { label: 'Shipping Fee', prop: 'shippingFee', width: 120, styleClass: 'text-right' },
  { label: 'Top-selling Products', prop: 'topSellingProducts', width: 180 },
  { label: 'Sales Growth Rate (%)', prop: 'salesGrowthRate', width: 160, styleClass: 'text-right' },
];

// Default configurations
export const SALE_REPORT_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
} as const;

// Messages
export const SALE_REPORT_MESSAGES = {
  SUCCESS: {
    DATA_EXPORTED: 'Report exported successfully',
    DATA_LOADED: 'Report data loaded successfully',
  },
  ERROR: {
    DATA_LOAD_FAILED: 'Failed to load report data',
    EXPORT_FAILED: 'Failed to export report',
  },
} as const;

// Trend indicators
export const TREND_INDICATORS = {
  up: '↗️ Up',
  down: '↘️ Down',
  stable: '→ Stable',
} as const;
